import { Loader2, X } from 'lucide-react'
import { formatColList, formatRowRange } from '@/components/lake/extraction-modal/helpers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { Case } from '@/services/api/cases'
import type { TabularColumn } from '@/services/api/lake'

export interface CreateScopePanelHeaderState {
  headerSource: 'file' | 'manual'
  headerRowIndex: number | null
  headerNames: string[]
  dataCols: TabularColumn[]
  emptyHeaders: boolean
}

export interface CreateScopePanelSelectionState {
  pendingRows: Set<number>
  pendingColumns: Set<number>
  noSelection: boolean
}

export interface CreateScopePanelSaveState {
  cases: Case[]
  selectedCaseId: string
  scopeNote: string
  saving: boolean
  canSave: boolean
  noHeaders: boolean
  noCaseSelected: boolean
}

export interface CreateScopePanelProps {
  header: CreateScopePanelHeaderState
  selection: CreateScopePanelSelectionState
  save: CreateScopePanelSaveState
  onHeaderSourceChange: (source: 'file' | 'manual') => void
  onClearHeaderRow: () => void
  onHeaderNameChange: (index: number, value: string) => void
  onSelectedCaseIdChange: (id: string) => void
  onScopeNoteChange: (note: string) => void
  onSave: () => void
}

/**
 * Pure create-mode right panel: header configuration (from-file / manual +
 * editable header names), the current data-selection summary, and the save-scope
 * form (case selector, note, validation summary, save button). All state arrives
 * via props; every interaction is a callback.
 */
export function CreateScopePanel({
  header,
  selection,
  save,
  onHeaderSourceChange,
  onClearHeaderRow,
  onHeaderNameChange,
  onSelectedCaseIdChange,
  onScopeNoteChange,
  onSave,
}: CreateScopePanelProps) {
  const { headerSource, headerRowIndex, headerNames, dataCols, emptyHeaders } = header
  const { pendingRows, pendingColumns, noSelection } = selection
  const { cases, selectedCaseId, scopeNote, saving, canSave, noHeaders, noCaseSelected } = save

  return (
    <div className="w-80 flex flex-col overflow-hidden bg-muted/20 max-md:w-full max-md:shrink-0 max-md:overflow-visible max-md:border-t">
      <div className="flex-1 overflow-y-auto max-md:flex-none max-md:overflow-visible">
        {/* HEADERS section */}
        <div className="p-3 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Headers
            </span>
            <div className="flex gap-1">
              <Button
                variant={headerSource === 'file' ? 'default' : 'outline'}
                size="sm"
                className="h-8 text-xs px-2 md:h-6"
                onClick={() => onHeaderSourceChange('file')}
              >
                From file
              </Button>
              <Button
                variant={headerSource === 'manual' ? 'default' : 'outline'}
                size="sm"
                className="h-8 text-xs px-2 md:h-6"
                onClick={() => onHeaderSourceChange('manual')}
              >
                Manual
              </Button>
            </div>
          </div>

          {headerRowIndex !== null && headerSource === 'file' && (
            <div className="mb-2 text-xs text-muted-foreground">
              Header from row <strong>{headerRowIndex}</strong>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-1 ml-1 text-xs"
                onClick={onClearHeaderRow}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          <div className="space-y-1">
            {headerNames.map((name, i) => (
              <div key={i} className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}</span>
                <Input
                  value={name}
                  onChange={e => onHeaderNameChange(i, e.target.value)}
                  placeholder={`Column ${i + 1}`}
                  className={[
                    'h-8 text-xs flex-1 md:h-6',
                    !name.trim() ? 'border-destructive' : '',
                  ].join(' ')}
                />
              </div>
            ))}
          </div>

          {emptyHeaders && (
            <p className="text-xs text-destructive mt-1">Column names cannot be empty</p>
          )}
          {dataCols.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {headerNames.length} headers defined
            </p>
          )}
        </div>

        {/* DATA SELECTION section */}
        <div className="p-3 border-b">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
            Data Selection
          </span>

          {noSelection ? (
            <p className="text-xs text-muted-foreground italic">
              No data selected — click or drag cells in the preview
            </p>
          ) : (
            <div className="text-xs space-y-1">
              <div className="text-muted-foreground">{formatRowRange([...pendingRows])}</div>
              <div className="text-muted-foreground truncate">
                {formatColList([...pendingColumns], dataCols)}
              </div>
              <div className="font-medium text-foreground pt-0.5">
                {pendingRows.size} row{pendingRows.size !== 1 ? 's' : ''} × {pendingColumns.size}{' '}
                col
                {pendingColumns.size !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>

        {/* ACTIONS section */}
        <div className="p-3 space-y-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
            Save Scope
          </span>

          {/* Case selector */}
          <div>
            <Label className="text-xs mb-1 block">Case</Label>
            <Select value={selectedCaseId} onValueChange={onSelectedCaseIdChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Select a case…" />
              </SelectTrigger>
              <SelectContent className="z-[300]">
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scope note */}
          <div>
            <Label className="text-xs mb-1 block">Note (optional)</Label>
            <Textarea
              value={scopeNote}
              onChange={e => onScopeNoteChange(e.target.value)}
              placeholder="e.g. Transactions Jan–Mar 2024"
              rows={2}
              className="text-xs resize-none"
            />
          </div>

          {/* Validation summary */}
          {!canSave && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              {noHeaders && <div className="text-destructive">• Define at least one header</div>}
              {emptyHeaders && !noHeaders && (
                <div className="text-destructive">• Fill in all header names</div>
              )}
              {noSelection && (
                <div className="text-destructive">• Select a data region in the preview</div>
              )}
              {noCaseSelected && <div className="text-destructive">• Select a case</div>}
            </div>
          )}

          {/* Save button */}
          <Button className="w-full" onClick={onSave} disabled={!canSave}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save scope
          </Button>
        </div>
      </div>
    </div>
  )
}
